import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { ChevronDown, Plus } from 'lucide-react';
import SchedulePage from './SchedulePage';
import EpmTooltip from '../TestWorkflow/EpmTooltip';
import { requiredSampleSize } from '../../../utils/epmStats';
import { dayFromToday, findLocationFieldMatches, keyToTest, structureOf, tagIdsOf, testId } from '../epmHelpers';
import { findMetric } from '../metricsCatalog';
import { buildOf, planOf, progressOf, scheduleOf } from '../TestWorkflow/stepData';
import { resolveTestSchedule } from './scheduleData';
import { useEPMContext } from '../../../contexts/EPMContext';
import { formatTestCardId } from '../../../utils/converter';
import { notifyAutoSaved } from '../TestWorkflow/useAutoSave';
import { useUserContext } from '../../../contexts/UserContext';
import { permission } from '../../../config/permission';
import DateRangePicker from '../../UI/DateRangePicker';
import { Input } from '../../UI';
import '../TestWorkflow/steps.css';

// "16 days" → "2 weeks and 2 days"; whole weeks read as such
const describeRun = (days) => {
    const weeks = Math.floor(days / 7);
    const rest = days % 7;
    if (!rest) return `${weeks} whole week${weeks === 1 ? '' : 's'}`;
    return `${weeks} week${weeks === 1 ? '' : 's'} and ${rest} day${rest === 1 ? '' : 's'}, not whole weeks`;
};

const PAST_SCHEDULED = ['LIVE', 'ANALYSING', 'CONCLUDED'];

// Live and scheduled tests whose date windows overlap this one and share configured location fields (conflict_role: Hard or Soft)
const findConflicts = (tests, testData, schedule, testLocations = []) => {
    const currentId = String(testId(testData) || testData?.key || '');
    const currentLocation = testData?.ideation?.location || testData?.location || {};

    const activeLocationFields = (testLocations || []).filter((loc) => {
        const role = (loc?.conflict_role || '').trim().toLowerCase();
        return role === 'hard' || role === 'soft';
    });

    const getCurrentRange = () => {
        if (schedule?.startDate && schedule?.endDate) {
            return {
                start: dayjs(schedule.startDate).startOf('day'),
                end: dayjs(schedule.endDate).startOf('day'),
            };
        }
        const startIso = testData?.schedule?.date_time;
        const endIso = testData?.schedule?.end_date_time;
        if (startIso && endIso) {
            return {
                start: dayjs(startIso).startOf('day'),
                end: dayjs(endIso).startOf('day'),
            };
        }
        return null;
    };

    const getOtherRange = (other) => {
        const startIso = other?.schedule?.date_time;
        const endIso = other?.schedule?.end_date_time;
        if (startIso && endIso) {
            const s = dayjs(startIso);
            const e = dayjs(endIso);
            if (s.isValid() && e.isValid()) {
                return {
                    start: s.startOf('day'),
                    end: e.startOf('day'),
                };
            }
        }
        const sched = structureOf(other).schedule || other?.schedule || {};
        if (sched.start_day != null && sched.end_day != null) {
            return {
                start: dayFromToday(sched.start_day).startOf('day'),
                end: dayFromToday(sched.end_day).startOf('day'),
            };
        }
        return null;
    };

    const curRange = getCurrentRange();

    const candidateTests = (tests || []).filter((test) => {
        if (test === testData) return false;
        const otherId = String(testId(test) || test?.key || '');
        if (currentId && otherId && currentId === otherId) return false;
        const status = (test?.status || '').toUpperCase();
        return status === 'LIVE' || status === 'SCHEDULED';
    });

    if (!activeLocationFields.length || !curRange) {
        return { checked: candidateTests.length, hard: [], soft: [] };
    }

    const hard = [];
    const soft = [];

    candidateTests.forEach((otherTest) => {
        const otherRange = getOtherRange(otherTest);
        if (!otherRange) return;

        // Check date window overlap
        const overlapsDates = curRange.start.isBefore(otherRange.end) && curRange.end.isAfter(otherRange.start);
        if (!overlapsDates) return;

        // Check location field matching
        const otherLocation = otherTest?.ideation?.location || otherTest?.location || {};
        const matches = findLocationFieldMatches(currentLocation, otherLocation, activeLocationFields);

        if (matches.length > 0) {
            const hasHardMatch = matches.some(
                (m) => (m.role || '').trim().toLowerCase() === 'hard'
            );
            const role = hasHardMatch ? 'Hard' : 'Soft';
            const conflictItem = {
                ...otherTest,
                test: otherTest,
                role,
                matches,
            };
            if (role === 'Hard') {
                hard.push(conflictItem);
            } else {
                soft.push(conflictItem);
            }
        }
    });

    return { checked: candidateTests.length, hard, soft };
};

const END_RULES = [
    { id: 'FIXED_SAMPLE', label: 'Fixed sample' },
    { id: 'FIXED_RATE', label: 'Fixed rate' },
    { id: 'SEQUENTIAL', label: 'Sequential', locked: true },
];

// Helper to convert UTC Date / ISO string to local YYYY-MM-DD and HH:mm
const parseUtcToLocal = (utcString) => {
    if (utcString) {
        const d = dayjs(utcString);
        if (d.isValid()) {
            return {
                date: d.format('YYYY-MM-DD'),
                time: d.format('HH:mm'),
            };
        }
    }
    return {
        date: '',
        time: '',
    };
};

// Helper to convert local YYYY-MM-DD and HH:mm to UTC ISO string
const localToUtcIso = (dateStr, timeStr) => {
    if (!dateStr) return null;
    return dayjs(`${dateStr}T${timeStr || '10:00'}`).toISOString();
};

// Workflow step 5: read-only once the test has gone live
const ScheduleStep = ({ testData, setTestData }) => {
    const { tests, testLocations = [], updateTest, tagsMap, stagingChecklist = [], productionChecklist = [] } = useEPMContext();

    const status = testData?.status;
    const isPast = PAST_SCHEDULED.includes(status);
    const testIdVal = testData?._id || testData?.id;

    const { checkPermission } = useUserContext();
    const canManageSchedule = checkPermission(permission.XCM_SCHEDULE_MANAGE);
    const canUpdateTest = checkPermission(permission.XCM_TESTS_UPDATE);
    const canEditSchedule = canManageSchedule && canUpdateTest;

    // const [goLiveFailOpen, setGoLiveFailOpen] = useState(false);
    // const [gateOpen, setGateOpen] = useState(true);

    const plan = useMemo(() => planOf(testData), [testData]);
    const schedule = useMemo(() => scheduleOf(testData), [testData]);
    const build = useMemo(() => buildOf(testData, { stagingChecklist, productionChecklist }), [testData, stagingChecklist, productionChecklist]);

    const initialStart = useMemo(() => parseUtcToLocal(testData?.schedule?.date_time), [testData?.schedule?.date_time]);
    const initialEnd = useMemo(() => parseUtcToLocal(testData?.schedule?.end_date_time), [testData?.schedule?.end_date_time]);

    const [startDate, setStartDate] = useState(initialStart.date);
    const [startTime, setStartTime] = useState(initialStart.time);
    const [endDate, setEndDate] = useState(initialEnd.date);
    const [endTime, setEndTime] = useState(initialEnd.time);

    // runDays is directly derived from start and end dates
    const runDays = useMemo(() => {
        if (!startDate || !endDate) return null;
        return Math.max(1, dayjs(endDate).diff(dayjs(startDate), 'day'));
    }, [startDate, endDate]);

    const wholeWeeks = runDays ? Math.ceil(runDays / 7) * 7 : 14;
    const lane = schedule.lane || testData?.ideation?.location?.funnel_step || 'PDP';

    const [endRule, setEndRule] = useState(() => {
        return testData?.schedule?.end_rule || 'FIXED_SAMPLE';
    });

    const handleScheduleChange = useCallback(async (updatedFields) => {
        if (!canEditSchedule) return;
        const nextSchedule = {
            ...(testData?.schedule || {}),
            ...updatedFields,
        };

        if (setTestData) {
            setTestData((prev) => ({
                ...prev,
                schedule: {
                    ...(prev?.schedule || {}),
                    ...updatedFields,
                },
            }));
        }

        if (testIdVal && updateTest) {
            try {
                await updateTest(testIdVal, { schedule: nextSchedule });
                notifyAutoSaved();
            } catch (err) {
                console.error("Failed to update test schedule:", err);
            }
        }
    }, [testData?.schedule, testIdVal, updateTest, setTestData]);

    useEffect(() => {
        const start = parseUtcToLocal(testData?.schedule?.date_time);
        setStartDate(start.date);
        setStartTime(start.time);

        const end = parseUtcToLocal(testData?.schedule?.end_date_time);
        setEndDate(end.date);
        setEndTime(end.time);
    }, [testData?.schedule?.date_time, testData?.schedule?.end_date_time]);

    useEffect(() => {
        if (testData?.schedule?.end_rule) {
            setEndRule(testData.schedule.end_rule);
        }
    }, [testData?.schedule?.end_rule]);

    const handleStartDateChange = (e) => {
        if (!canEditSchedule) return;
        const newDate = e.target.value;
        setStartDate(newDate);
        let nextEndDate = endDate;
        if (newDate && endDate && newDate > endDate) {
            nextEndDate = newDate;
            setEndDate(nextEndDate);
        }
        const effectiveStartTime = newDate ? (startTime || '10:00') : '';
        if (!startTime && newDate) setStartTime(effectiveStartTime);

        const utcStart = localToUtcIso(newDate, effectiveStartTime);
        const utcEnd = localToUtcIso(nextEndDate, endTime || '18:00');
        handleScheduleChange({
            date_time: utcStart,
            end_date_time: nextEndDate ? utcEnd : null,
        });
    };

    const handleStartTimeChange = (e) => {
        if (!canEditSchedule) return;
        const newTime = e.target.value;
        setStartTime(newTime);
        if (startDate) {
            const utcStart = localToUtcIso(startDate, newTime);
            const utcEnd = localToUtcIso(endDate, endTime || '18:00');
            handleScheduleChange({
                date_time: utcStart,
                end_date_time: endDate ? utcEnd : null,
            });
        }
    };

    const handleEndDateChange = (e) => {
        if (!canEditSchedule) return;
        const newDate = e.target.value;
        setEndDate(newDate);
        const effectiveEndTime = newDate ? (endTime || '18:00') : '';
        if (!endTime && newDate) setEndTime(effectiveEndTime);

        const utcStart = localToUtcIso(startDate, startTime || '10:00');
        const utcEnd = localToUtcIso(newDate, effectiveEndTime);
        handleScheduleChange({
            date_time: startDate ? utcStart : null,
            end_date_time: utcEnd,
        });
    };

    const handleEndTimeChange = (e) => {
        if (!canEditSchedule) return;
        const newTime = e.target.value;
        setEndTime(newTime);
        if (endDate) {
            const utcStart = localToUtcIso(startDate, startTime || '10:00');
            const utcEnd = localToUtcIso(endDate, newTime);
            handleScheduleChange({
                date_time: startDate ? utcStart : null,
                end_date_time: utcEnd,
            });
        }
    };

    const handleExtendDays = () => {
        if (!startDate || !runDays) return;
        const newEndDate = dayjs(startDate).add(wholeWeeks, 'day').format('YYYY-MM-DD');
        setEndDate(newEndDate);
        const utcStart = localToUtcIso(startDate, startTime || '10:00');
        const utcEnd = localToUtcIso(newEndDate, endTime || '18:00');
        handleScheduleChange({
            date_time: utcStart,
            end_date_time: utcEnd,
        });
    };

    const baseline = findMetric(plan.primary_metric)?.baseline ?? plan.metric?.baseline ?? null;
    const perVariant = requiredSampleSize({ baseline, mde: (plan.mde || 3) / 100 });

    const conflicts = useMemo(
        () => findConflicts(tests, testData, { startDate, endDate }, testLocations),
        [tests, testData, startDate, endDate, testLocations],
    );
    // Gate from the saved build record and current conflicts.
    const signOffValid = !!build.production.signed_by;
    const signOffCleared = !signOffValid && (build.lost_day != null || /after sign-off/i.test(build.note || ''));
    const hardCount = conflicts.hard.length;

    const progress = status === 'LIVE' ? progressOf(testData) : null;

    const goLiveLong = startDate ? dayjs(startDate).format('ddd D MMM YYYY') : 'Not scheduled';
    const goEndLong = endDate ? dayjs(endDate).format('ddd D MMM YYYY') : 'Not scheduled';

    const conflictLine = () => {
        if (isPast) return <span>Conflict check passed at scheduling — no live test shared the location or schedule</span>;
        if (!startDate || !endDate) return <span>Set start and end dates to check for lane and location conflicts</span>;
        if (conflicts.hard.length) {
            const first = conflicts.hard[0];
            const matchDetails = first.matches?.length
                ? first.matches.map((m) => `${m.label}: ${m.value}`).join(' · ')
                : '';
            return (
                <span>
                    Hard conflict — overlaps with {formatTestCardId(first)} ({first.name || 'Untitled'})
                    {matchDetails ? ` on ${matchDetails}` : ''}
                    {conflicts.hard.length > 1 ? ` (+${conflicts.hard.length - 1} more)` : ''}
                </span>
            );
        }
        if (conflicts.soft.length) {
            const first = conflicts.soft[0];
            const matchDetails = first.matches?.length
                ? first.matches.map((m) => `${m.label}: ${m.value}`).join(' · ')
                : '';
            return (
                <span>
                    No hard conflicts · soft interaction risk with {conflicts.soft.map((test) => formatTestCardId(test)).join(', ')}
                    {matchDetails ? ` (${matchDetails})` : ''}
                </span>
            );
        }

        return <span>No conflicts at this slot — checked against {conflicts.checked} live and scheduled test{conflicts.checked === 1 ? '' : 's'}</span>;
    };

    const endRuleText = () => {
        const sample = perVariant.toLocaleString('en-GB');
        const verb = isPast && status !== 'LIVE' ? 'Ended' : 'Ends';
        if (endRule === 'FIXED_RATE') {
            return <>{verb} at fixed conversion rate <span className="text-stride-muted">— stops when fixed rate target is reached.</span></>;
        }
        return <>{verb} at {sample} per variant <span className="inline-block px-2.5 py-0.5 border border-border-subtle rounded-full bg-surface-alt text-[11.5px] text-stride-muted mx-1">recommended</span> <span className="text-stride-muted">— on information, however traffic moves.</span></>;
    };

    return (
        <div className="epm-plan epm-build">
            <header className="epm-workflow-step__heading epm-plan__heading">
                <span>05</span>
                <div>
                    <h2>Schedule</h2>
                    <p>{isPast
                        ? 'What was scheduled. Read-only now that the test has gone live.'
                        : 'Choose the start (go-live) and end date/time to schedule the experiment.'}</p>
                </div>
            </header>

            <div className="flex flex-col gap-8">
                {/* Schedule window card */}
                <section className="epm-plan__card mb-0" id="epm-schedule-dates">
                    <div className="epm-plan__section-heading mb-[14px]">
                        <span className="text-[10px] font-bold tracking-[.08em] uppercase text-stride-muted-light">
                            Schedule window <span className="text-stride-muted-light normal-case tracking-normal font-normal">
                                · {isPast ? (status === 'LIVE' ? `live since ${goLiveLong}` : `ran ${goLiveLong} – ${goEndLong}`) : (startDate && endDate ? 'scheduled window' : 'not yet scheduled')}
                            </span>
                        </span>
                    </div>

                    {isPast ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="stride-form-label">
                                    Start (Go live)
                                </label>
                                <div className="flex gap-2 items-center">
                                    <span className="epm-step-readonly flex-1">{status === 'LIVE' ? 'Live since' : 'Went live'} {goLiveLong}</span>
                                    <span className="epm-step-readonly w-[80px]">{startTime || '—'}</span>
                                    <span className="text-[12px] text-stride-muted-light">🔒</span>
                                </div>
                            </div>
                            <div>
                                <label className="stride-form-label">
                                    End date & time
                                </label>
                                <div className="flex gap-2 items-center">
                                    <span className="epm-step-readonly flex-1">Ended {goEndLong}</span>
                                    <span className="epm-step-readonly w-[80px]">{endTime || '—'}</span>
                                    <span className="text-[12px] text-stride-muted-light">🔒</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-4">
                            {canEditSchedule && (
                                <div className="flex items-center justify-between pb-3 border-b border-border-subtle flex-wrap gap-2">
                                    <div>
                                        <span className="text-xs font-semibold text-stride-heading">Experiment Duration</span>
                                        <p className="text-xs text-stride-muted">Pick a preset or custom date range to auto-fill start and end dates.</p>
                                    </div>
                                    <DateRangePicker
                                        startDate={startDate}
                                        endDate={endDate}
                                        onDateChange={(start, end) => {
                                            if (start) handleStartDateChange({ target: { value: start } });
                                            if (end) handleEndDateChange({ target: { value: end } });
                                        }}
                                        label="Duration Presets"
                                        align="right"
                                        hideLabel={false}
                                    />
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <div className="flex items-center gap-1.5 mb-1.5">
                                        <label className="stride-form-label !mb-0">
                                            Start (Go live)
                                        </label>
                                        <EpmTooltip content="A weekday-morning start means the first hours of data arrive during working time, with people around if something breaks. A Friday-evening start gives a weekend-shaped first impression and nobody on hand." />
                                    </div>
                                    <div className="flex gap-3 items-center">
                                        <Input
                                            type="date"
                                            disabled={!canEditSchedule}
                                            className="flex-1"
                                            value={startDate}
                                            onChange={handleStartDateChange}
                                        />
                                        <Input
                                            type="time"
                                            disabled={!canEditSchedule}
                                            className="w-[130px]"
                                            value={startTime}
                                            onChange={handleStartTimeChange}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center gap-1.5 mb-1.5">
                                        <label className="stride-form-label !mb-0">
                                            End date & time
                                        </label>
                                        <EpmTooltip content="The scheduled conclusion of the experiment. Concluding on a whole-week boundary prevents day-of-week seasonality bias." />
                                    </div>
                                    <div className="flex gap-3 items-center">
                                        <Input
                                            type="date"
                                            min={startDate}
                                            disabled={!canEditSchedule}
                                            className="flex-1"
                                            value={endDate}
                                            onChange={handleEndDateChange}
                                        />
                                        <Input
                                            type="time"
                                            disabled={!canEditSchedule}
                                            className="w-[130px]"
                                            value={endTime}
                                            onChange={handleEndTimeChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col gap-1 mt-[14px]">
                        <div className="flex items-center text-[13px] text-stride-muted">
                            <span>
                                {runDays
                                    ? `${progress ? `Runs ${runDays} days · day ${progress.day} of ${progress.total} today` : `${isPast ? 'Ran' : 'Runs'} ${runDays} days`}. That is ${describeRun(runDays)}.`
                                    : 'Set start and end dates to calculate the run duration.'}
                            </span>
                            <div className="flex-1"></div>
                            {!isPast && canEditSchedule && runDays && runDays !== wholeWeeks && (
                                <button type="button" onClick={handleExtendDays} className="px-[11px] py-[5px] border border-border rounded-md bg-white text-[12px] text-stride-body hover:bg-surface cursor-pointer">Extend to {wholeWeeks} days</button>
                            )}
                        </div>
                        {runDays > 84 && (
                            <p className="text-xs text-amber-600 mt-1">
                                Warning: Run length is over 12 weeks ({runDays} days). Tests longer than 12 weeks risk audience drift and seasonality effects.
                            </p>
                        )}
                        {startDate && endDate && runDays < 1 && (
                            <p className="text-xs text-red-500 mt-1">
                                End date and time must be strictly after start date and time (minimum 1 day).
                            </p>
                        )}
                    </div>

                    <div className="flex items-center gap-2 text-[13px] text-stride-body mt-3" id="epm-schedule-conflicts">
                        <span className={`font-bold ${hardCount ? 'text-danger' : (!startDate || !endDate ? 'text-stride-muted-light' : 'text-stride-heading')}`}>{hardCount ? '⚠' : (!startDate || !endDate ? '○' : '✓')}</span>
                        {conflictLine()}
                        <EpmTooltip content="Checked against every live and scheduled test on the same component and audience. A hard conflict blocks scheduling. Moving the date here re-runs the check and updates the Schedule page." />
                    </div>
                </section>

                {/* End rule card: hidden for now */}

                {/* If go-live fails collapsible — hidden for now
                {!isPast && (
                    <div className="epm-plan__collapsible flat">
                        <div className={`epm-plan__collapsible-header ${goLiveFailOpen ? 'is-open' : ''}`} onClick={() => setGoLiveFailOpen(!goLiveFailOpen)}>
                            <ChevronDown size={15} />
                            <span className="epm-plan__collapsible-title text-[13px]">If go-live fails</span>
                            <span className="epm-plan__collapsible-subtitle text-[13px]">Retry, then Blocked and notify</span>
                        </div>
                        {goLiveFailOpen && (
                            <div className="epm-plan__collapsible-body pl-6 pt-1 pb-2">
                                <ul className="m-0 pl-[18px] text-[13.5px] text-[#4d5873] list-disc">
                                    <li className="my-1.5">Adobe Target activation is retried with backoff.</li>
                                    <li className="my-1.5">If it still fails, the test moves to <strong>Blocked</strong> and the owner and Lead are notified.</li>
                                    <li className="my-1.5">Once the cause is fixed it returns to <strong>Scheduled</strong> with a new go-live time.</li>
                                </ul>
                            </div>
                        )}
                    </div>
                )}
                */}
            </div>

            {/* Nextbar */}

        </div>
    );
};

const Schedule = ({ isStep = false, testData, setTestData }) => {
    if (isStep) return <ScheduleStep key={String(testId(testData))} testData={testData} setTestData={setTestData} />;
    return <SchedulePage />;
};

export default Schedule;
